import React from "react";
import { Link } from "react-router-dom";
import { FilePlus2, TrendingUp, ShieldCheck, ShieldAlert, Sparkles, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kpi } from "@/components/dashboard/Kpi";
import { RiskDistributionChart } from "@/components/dashboard/RiskDistributionChart";
import { RecentAssessments } from "@/components/dashboard/RecentAssessments";

const PRO_PERKS = [
  "Downloadable PDF evidence reports",
  "AI risk summaries on every assessment",
  "Evidence Centre with search & saved reports",
  "Full risk analytics & trends dashboard",
];

export const FreeDashboard = ({ stats, user }) => {
  const used = stats.used_this_month;
  const limit = stats.monthly_limit;
  const remaining = limit != null ? Math.max(limit - used, 0) : null;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold">Welcome back{user?.business_name ? `, ${user.business_name}` : ""}.</h2>
        <p className="text-sm text-muted-foreground">Here's a snapshot of your booking risk activity.</p>
      </div>

      {limit != null && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/[0.03] p-4 sm:flex-row sm:items-center" data-testid="free-plan-banner">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-sm"><span className="font-medium">{used}/{limit}</span> free assessments used this month{remaining != null ? ` · ${remaining} remaining` : ""}. Upgrade for unlimited checks + AI summaries.</p>
          </div>
          <Link to="/app/pricing"><Button size="sm" data-testid="upgrade-banner-button">Upgrade to Pro Host</Button></Link>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Assessments this month" value={stats.total_this_month} sub={`${stats.total_all_time} all time`} icon={FilePlus2} accent="hsl(var(--primary))" testid="kpi-total-month" />
        <Kpi label="Average risk score" value={stats.average_score} sub="across all assessments" icon={TrendingUp} accent="hsl(var(--primary))" testid="kpi-average-score" />
        <Kpi label="Low risk bookings" value={stats.low} icon={ShieldCheck} accent="#2f9e6b" testid="kpi-low" />
        <Kpi label="High risk bookings" value={stats.high} icon={ShieldAlert} accent="#c93838" testid="kpi-high" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <RiskDistributionChart stats={stats} />
        <RecentAssessments recent={stats.recent} />
      </div>

      {/* Upgrade prompt highlighting Pro features */}
      <div className="rounded-2xl border border-primary/20 bg-primary/[0.03] p-6" data-testid="free-upgrade-card">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" />
          <h3 className="font-display text-base font-semibold">Unlock the professional risk platform</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Pro Host turns HostGuard into a complete evidence &amp; risk management toolkit.</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {PRO_PERKS.map((p) => (
            <div key={p} className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4 text-[hsl(var(--risk-low))]" />{p}</div>
          ))}
        </div>
        <Link to="/app/pricing"><Button className="mt-5 gap-2" data-testid="free-upgrade-cta">Upgrade to Pro Host <ArrowRight className="h-4 w-4" /></Button></Link>
      </div>
    </div>
  );
};
